'use client';

import { FC, ReactNode } from 'react';
import { SxProps, Theme } from '@mui/material/styles';
import { StyledButton } from './styles';

export type ButtonContainedDefaultTheme = 'light' | 'accent';
export type ButtonOutlinePrimaryDefaultTheme = 'light' | 'primary';
export type ButtonTextDefaultTheme = 'light' | 'primary';

type ButtonVariant = 'outline-primary' | 'contained' | 'text';

interface ButtonDefaultPropsBase {
  onClick?: () => void;
  children: ReactNode;
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
  sx?: SxProps<Theme>;
  disabled?: boolean;
}

type ButtonDefaultProps = ButtonDefaultPropsBase &
  (
    | { variant?: 'contained'; theme?: ButtonContainedDefaultTheme }
    | { variant?: 'outline-primary'; theme?: ButtonOutlinePrimaryDefaultTheme }
    | { variant?: 'text'; theme?: ButtonTextDefaultTheme }
    | { variant?: never; theme?: never }
  );

const resolveMuiVariant = (
  variant: ButtonVariant | undefined,
): 'contained' | 'text' | 'outlined' => {
  const variantMap: Record<ButtonVariant, 'contained' | 'text' | 'outlined'> = {
    contained: 'contained',
    text: 'text',
    'outline-primary': 'outlined',
  };

  return variant ? variantMap[variant] : 'outlined';
};

export const ButtonDefault: FC<ButtonDefaultProps> = ({
  onClick,
  children,
  icon,
  iconPosition = 'left',
  variant = 'outline-primary',
  theme,
  sx,
  disabled,
}) => {
  const startIcon = iconPosition === 'left' ? icon : undefined;
  const endIcon = iconPosition === 'right' ? icon : undefined;

  const defaultTheme: Record<ButtonVariant, string> = {
    contained: 'light',
    'outline-primary': 'light',
    text: 'light',
  };

  const resolvedTheme = theme || defaultTheme[variant || 'outline-primary'];
  const fullTheme = `${variant || 'contained'}-${resolvedTheme}`;

  return (
    <StyledButton
      variant={resolveMuiVariant(variant)}
      $theme={fullTheme}
      onClick={onClick}
      startIcon={startIcon}
      endIcon={endIcon}
      sx={sx}
      disabled={disabled}
    >
      {children}
    </StyledButton>
  );
};
